import { atom, AtomEffect } from 'recoil';
import { Tour, routeToStopLocation, Border } from '@app/shared/models/tour.type';

const ROUTE_STORAGE_KEY = 'saved_route_to_stop';

// Effect to sync routeToStopLocation with localStorage
const localStorageEffect: AtomEffect<routeToStopLocation | null> = ({ setSelf, onSet }) => {
	// On load, read from localStorage — only restore if tourId matches current tour
	if (typeof window !== 'undefined') {
		try {
			const saved = localStorage.getItem(ROUTE_STORAGE_KEY);
			if (saved) {
				const parsed = JSON.parse(saved);
				// Extract current tour ID from URL (e.g., /tour/TOUR_ID or ?tour=TOUR_ID)
				const urlTourId = window.location.pathname.split('/').filter(Boolean)[0]
					|| new URLSearchParams(window.location.search).get('tour');
				// Only restore if no tourId stored (legacy) or it matches current tour
				if (!parsed.tourId || parsed.tourId === urlTourId) {
					setSelf(parsed);
				} else {
					localStorage.removeItem(ROUTE_STORAGE_KEY);
				}
			}
		} catch (e) {
			// Invalid JSON, ignore
		}
	}

	// On change, write to localStorage (or remove if null)
	onSet((newValue, _, isReset) => {
		if (typeof window === 'undefined') return;
		try {
			if (isReset || newValue === null) {
				localStorage.removeItem(ROUTE_STORAGE_KEY);
			} else {
				localStorage.setItem(ROUTE_STORAGE_KEY, JSON.stringify(newValue));
			}
		} catch (e) {
			// Storage error, ignore
		}
	});
};

export const atom_tourState = atom<Tour | undefined>({
	key: 'Tour',
	default: undefined,
});

export const atom_originalTourState = atom<Tour | undefined>({
	key: 'Original_Tour',
	default: undefined,
});

export const atom_isDesktop = atom<boolean>({
	key: 'IsDesktop',
	default: false,
});

// Mobile platform / PWA / iframe detection now lives in @app/shared/utils/env.
// They were converted from atoms to plain consts since they're computed once at boot
// and never change at runtime.

export const atom_routeToStopLocation = atom<routeToStopLocation | null>({
	key: 'routeLocationToStop',
	default: null,
	effects: [localStorageEffect],
});

export const atom_borders = atom<Border[] | null>({
	key: 'Borders',
	default: null,
});

// Navigation mode - single source of truth for "user is navigating":
//  - when true: RouteListContent shows + tracker runs arrival/off-route checks
//  - when false: no panel, no tracking
// Defaults to false so tracker doesn't flip on prematurely; flipped true by flows that fetch a route.
export const atom_isNavigationMode = atom<boolean>({
	key: 'IsNavigationMode',
	default: false,
});

// Show navigation bubble - becomes true after first minimize, stays until route is cleared
export const atom_showNavigationBubble = atom<boolean>({
	key: 'ShowNavigationBubble',
	default: false,
});

// Current navigation step index - tracks which step user is on during navigation
export const atom_currentNavigationStep = atom<number>({
	key: 'CurrentNavigationStep',
	default: 0,
});

// FitBounds request - when set, the map will fit to these bounds
export interface FitBoundsRequest {
	bounds: [[number, number], [number, number]]; // [[south, west], [north, east]]
	timestamp: number; // to trigger even if same bounds
	bottomPadding?: number; // extra bottom padding for panel height
}

export const atom_fitBoundsRequest = atom<FitBoundsRequest | null>({
	key: 'FitBoundsRequest',
	default: null,
});

// Smart Agent visibility state
export const atom_showSmartAgent = atom<boolean>({
	key: 'ShowSmartAgent',
	default: false,
});
